
import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { MenuBlockConfiguration, MenuItem } from "@/types/block-configurations";
import {
  TypographyControls,
  AlignmentControls,
  PaddingControls,
  ResponsiveControls,
} from "../shared";

interface MenuBlockPropertiesProps {
  config: MenuBlockConfiguration;
  onChange: (config: MenuBlockConfiguration) => void;
}

export function MenuBlockProperties({ config, onChange }: MenuBlockPropertiesProps) {
  const [newText, setNewText] = useState('');
  const [newHref, setNewHref] = useState('');

  const updateField = <K extends keyof MenuBlockConfiguration>(
    field: K,
    value: MenuBlockConfiguration[K]
  ) => {
    onChange({ ...config, [field]: value });
  };

  const items = config.items ?? [];

  const addItem = () => {
    if (!newText.trim()) return;
    const item: MenuItem = { text: newText.trim(), href: newHref.trim() || '#' };
    updateField('items', [...items, item]);
    setNewText('');
    setNewHref('');
  };

  const removeItem = (index: number) => {
    updateField('items', items.filter((_, i) => i !== index));
  };

  const updateItem = (index: number, field: keyof MenuItem, value: string) => {
    updateField('items', items.map((item, i) => i === index ? { ...item, [field]: value } : item));
  };

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs font-medium mb-1 block">Layout</Label>
          <Select
            value={config.layout}
            onChange={(e) => updateField('layout', e.target.value as MenuBlockConfiguration['layout'])}
          >
            <option value="horizontal">Horizontal</option>
            <option value="vertical">Vertical</option>
          </Select>
        </div>
        <div>
          <Label className="text-xs font-medium mb-1 block">Item Spacing (px)</Label>
          <Input
            type="number"
            value={config.itemSpacing}
            onChange={(e) => {
              const parsed = parseInt(e.target.value);
              updateField('itemSpacing', Number.isNaN(parsed) ? 15 : parsed);
            }}
            min={0}
            max={60}
          />
        </div>
      </div>

      {config.layout === 'horizontal' && (
        <div>
          <Label className="text-xs font-medium mb-1 block">Separator</Label>
          <Input
            type="text"
            value={config.separator ?? '|'}
            onChange={(e) => updateField('separator', e.target.value)}
            maxLength={5}
          />
          <p className="text-xs text-muted-foreground mt-1">
            Shown between items — clear to remove the separator
          </p>
        </div>
      )}

      <Accordion type="multiple" defaultValue={["items"]} className="w-full">

        <AccordionItem value="items">
          <AccordionTrigger>Menu Items</AccordionTrigger>
          <AccordionContent>
            <div className="space-y-2">
              {items.map((item, i) => (
                <div key={i} className="flex gap-2 items-center">
                  <Input
                    type="text"
                    value={item.text}
                    onChange={(e) => updateItem(i, 'text', e.target.value)}
                    placeholder="Label"
                    className="flex-1"
                  />
                  <Input
                    type="url"
                    value={item.href}
                    onChange={(e) => updateItem(i, 'href', e.target.value)}
                    placeholder="https://..."
                    className="flex-1"
                  />
                  <button
                    type="button"
                    onClick={() => removeItem(i)}
                    className="shrink-0 rounded p-1.5 text-destructive hover:bg-destructive/10 transition-colors"
                    title="Remove"
                  >
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              ))}
              <div className="flex gap-2">
                <Input
                  type="text"
                  value={newText}
                  onChange={(e) => setNewText(e.target.value)}
                  placeholder="Label"
                  className="flex-1"
                  onKeyDown={(e) => { if (e.key === 'Enter') addItem(); }}
                />
                <Input
                  type="url"
                  value={newHref}
                  onChange={(e) => setNewHref(e.target.value)}
                  placeholder="https://..."
                  className="flex-1"
                  onKeyDown={(e) => { if (e.key === 'Enter') addItem(); }}
                />
                <button
                  type="button"
                  onClick={addItem}
                  className="shrink-0 rounded border border-border bg-background px-2 py-1.5 text-xs hover:bg-accent transition-colors"
                >
                  Add
                </button>
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="typography">
          <AccordionTrigger>Typography</AccordionTrigger>
          <AccordionContent>
            <TypographyControls
              value={config.typography}
              onChange={(typography) => updateField('typography', typography)}
            />
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="alignment">
          <AccordionTrigger>Alignment</AccordionTrigger>
          <AccordionContent>
            <AlignmentControls
              value={config.alignment}
              onChange={(alignment) => updateField('alignment', alignment)}
              showVertical={false}
            />
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="spacing">
          <AccordionTrigger>Spacing</AccordionTrigger>
          <AccordionContent>
            <PaddingControls
              value={config.padding}
              onChange={(padding) => updateField('padding', padding)}
            />
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="responsive">
          <AccordionTrigger>Responsive</AccordionTrigger>
          <AccordionContent>
            <ResponsiveControls
              value={config.responsive}
              onChange={(responsive) => updateField('responsive', responsive)}
            />
          </AccordionContent>
        </AccordionItem>

      </Accordion>
    </div>
  );
}
